"use client";

import { useSession } from "next-auth/react";
import { EventJourneyStatus } from "@prisma/client";

import { useEffect, useState } from "react";
import { GoInfo } from "react-icons/go";
import { toast } from "react-toastify";
import { api } from "~/utils/api";

import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "../shadcn/Tooltip";
import EventJourneyStatusChangeConfirmationModal from "./EventJourneyStatusChangeConfirmationModal";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "~/components/shadcn/Select";
import { UserPermissions } from "~/server/api/helpers/permissionHelpers";
import { SideBarState } from "~/state/leftSideBarStore";

type EventJourneyStatusDropdownProps = {
    eventSlug?: string;
    permissions?: UserPermissions;
    setSideBarState?: (_state: SideBarState) => void;
    onClick?: (event: React.MouseEvent<HTMLSelectElement, MouseEvent>) => void;
};

//TODO: we can probably optimise/clean this...
const EventJourneyStatusDropdown = ({
    eventSlug,
    permissions,
    setSideBarState,
}: EventJourneyStatusDropdownProps) => {
    const { data: session } = useSession();
    const { data: event } = api.event.getEventJounreyStatus.useQuery(
        { eventSlug: eventSlug ?? "" },
        { enabled: !!eventSlug },
    );

    const utils = api.useUtils();
    const updateEjsMutation = api.event.updateEventJourneyStatus.useMutation({
        onSuccess: () => {
            utils.event.getBySlug.invalidate();
        },
    });

    const [inputValue, setInputValue] = useState<string>("");
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (event?.eventJourneyStatus) {
            setInputValue(event.eventJourneyStatus);
        }
    }, [event?.eventJourneyStatus]);

    // Handle change event for input
    const handleInputChange = async (newValue: EventJourneyStatus) => {
        setInputValue(newValue);
        setIsVisible(true);
    };

    const confirmEjsChange = async () => {
        const loadingToastId = toast.loading("Updating Event Status", {
            toastId: "update-event-status-loading",
        });

        await updateEjsMutation.mutateAsync(
            {
                eventSlug: eventSlug ?? "",
                status: inputValue,
            },
            {
                onSuccess: () => {
                    toast.update(loadingToastId, {
                        render: "Event status updated successfully",
                        type: "success",
                        isLoading: false,
                        autoClose: 800,
                    });

                    utils.event.getEventJounreyStatus.invalidate({
                        eventSlug: eventSlug ?? "",
                    });
                },
                onError: (e) => {
                    console.error(e);
                    toast.update(loadingToastId, {
                        render: "Failed to update event status",
                        type: "error",
                        isLoading: false,
                        autoClose: 800,
                    });
                },
                onSettled: () => {
                    setIsVisible(false);
                    setSideBarState ? setSideBarState("closed") : "";
                },
            },
        );
    };

    const cancelEjsChange = async () => {
        setInputValue(event?.eventJourneyStatus ?? "");
    };

    const isEventOwner = event?.userId === session?.user.id;

    // is Admin, CommunityManager or It's a personal event owned by logged in User
    const isAdminOrCommunityManagerOrOwner =
        isEventOwner || permissions?.isAdmin || permissions?.isManager;

    const isAllowedToChange =
        permissions?.isAdmin || permissions?.isManager || permissions?.isHost;

    return (
        <div>
            <div className="relative flex w-full flex-col gap-1">
                <label className="flex gap-1">
                    <span>Event Status</span>
                    <TooltipProvider delayDuration={0}>
                        <Tooltip>
                            <TooltipTrigger>
                                <GoInfo className="ml-2 h-4 w-4" />
                            </TooltipTrigger>
                            <TooltipContent sideOffset={-100}>
                                <p>
                                    This is the status of the event. Your event
                                    will only appear on the main feed if it is{" "}
                                    {EventJourneyStatus.PUBLISHED}
                                </p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </label>

                <Select
                    value={inputValue}
                    onValueChange={handleInputChange}
                    disabled={!isAllowedToChange}
                >
                    <SelectTrigger
                        className={`
                            w-full rounded-[10px] border border-v3-N-500 bg-v3-N-500
                            px-3 py-2
                        `}
                    >
                        <SelectValue placeholder="Event Status" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={EventJourneyStatus.ARCHIVED}>
                            Archived
                        </SelectItem>
                        <SelectItem value={EventJourneyStatus.DELETED}>
                            Deleted
                        </SelectItem>
                        <SelectItem
                            value={EventJourneyStatus.PUBLISHED}
                            disabled={!isAdminOrCommunityManagerOrOwner}
                        >
                            Published
                        </SelectItem>
                        <SelectItem
                            value={EventJourneyStatus.DRAFT_PROPOSED}
                            disabled={!isAdminOrCommunityManagerOrOwner}
                        >
                            Draft Proposal
                        </SelectItem>
                        <SelectItem
                            value={EventJourneyStatus.PROPOSED}
                            disabled={!isAdminOrCommunityManagerOrOwner}
                        >
                            Proposed
                        </SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <EventJourneyStatusChangeConfirmationModal
                isVisible={isVisible}
                setIsVisible={setIsVisible}
                newEjs={inputValue as EventJourneyStatus}
                confirmationCallback={confirmEjsChange}
                cancelCallback={cancelEjsChange}
            />
        </div>
    );
};

export default EventJourneyStatusDropdown;
